// sendUnlockMessage.js
'use strict';
import fetch from '../resource/customFetch.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import handleFetchError from '../resource/handleFetchError.js';
import { getKeySystem } from '../resource/getDrmInfo.js';
import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';
import {
  CONFIG_PRIORITY_PRESENCE_USER,
  CONFIG_PRIORITY_PRESENCE_CLIENT,
} from '../resource/configPriority.js';
import getCurrentLanguage from '../resource/getCurrentLanguage.js';
import { gtm as gtmDebug } from '../resource/debug.js';
import trackMixpanel, { EventTypes } from '../resource/mixpanel.js';

import getMeData from '../selector/getMeData.js';
import getUserData from '../selector/getUserData.js';
import getMessageData from '../selector/getMessageData.js';
import getMessageDataWithVariable, {
  VariableName,
} from '../selector/getMessageDataWithVariable.js';
import getRouterData from '../selector/getRouterData.js';
import getTimestampOffset, {
  Accuracy,
} from '../selector/getTimestampOffset.js';
import getAbTestingData from '../selector/getAbTestingData.js';
import getMessageUnlockPrice from '../selector/getMessageUnlockPrice.js';
import { getIsDrmRequirementMatched } from '../selector/getDrmKeySystemAndRobustness.js';
import getOperationData from '../selector/getOperationData.js';

import { replace } from '../action/navigationAction.js';
import toggleModal from '../action/toggleModal.js';
import addModal from '../action/addModal.js';
import login from '../action/login.js';
import fetchDrmLicenseAvailability from '../action/fetchDrmLicenseAvailability.js';
import deductDiamond from '../action/deductDiamond.js';
import toggleDrmNotSupportAlert, {
  INTERVAL_TO_SHOW_NEXT_ALERT,
} from '../action/toggleDrmNotSupportAlert.js';
import fetchOembedData from '../action/fetchOembedData.js';
import fetchMessageDetail from '../action/fetchMessageDetail.js';

import {
  SET_NETWORKING_FETCHING,
  SET_NETWORKING_SUCCESS,
  SET_NETWORKING_ERROR,
} from '../ActionTypes.js';

const freezoneDetailReg = /\/freezone\/[a-z\d]+/i; // TODO: remote config
const log = gtmDebug.extend('log:GtmLoader');

/**
 * Send unlock message
 * @kind action
 * @param {string} {id} - message id.
 * @param {DrmFeatureName} {drmFeatureName} - drm feature name.
 * @param {boolean} {shouldCheckDrm} - should check drm.
 * @param {boolean} {shouldCheckPrice} - should check price before unlock message to prevent price inconsist.
 * @param {boolean} {shouldOpenMessagePackUpsellingModal} - should open message pack upselling modal.
 * @return {Promise} Action promise.
 */
const sendUnlockMessage =
  ({
    id,
    shouldCheckDrm = false,
    shouldCheckPrice = true,
    shouldOpenMessagePackUpsellingModal = false,
    drmFeatureName,
  }) =>
  async (dispatch, getState) => {
    const token = getMeData(getState(), 'token');
    if (!token) {
      return dispatch(login());
    }

    if (shouldCheckDrm) {
      const checkDrmCapability = (
        await import('../action/checkDrmCapability.js')
      ).default;
      await dispatch(checkDrmCapability({ isEarlyLeave: true }));
      const isMatched = getIsDrmRequirementMatched(getState(), drmFeatureName);
      const lastNotSupportAlertTimestamp = getMeData(
        getState(),
        'lastNotSupportAlertTimestamp'
      );
      const needOpenNotSupportAlert =
        Date.now() > lastNotSupportAlertTimestamp + INTERVAL_TO_SHOW_NEXT_ALERT;
      if (!isMatched && needOpenNotSupportAlert) {
        return dispatch(
          toggleDrmNotSupportAlert({
            actionName: 'sendUnlockMessage',
            actionPayload: {
              id,
              shouldCheckDrm: false,
              shouldCheckPrice: true,
              drmFeatureName,
            },
          })
        );
      }
    }

    const abPriority = getMeData(getState(), 'id')
      ? CONFIG_PRIORITY_PRESENCE_USER
      : CONFIG_PRIORITY_PRESENCE_CLIENT;
    const abToken = getAbTestingData(
      getState(),
      undefined,
      'abToken',
      abPriority
    );

    if (abToken) {
      const abTokenExp = getAbTestingData(
        getState(),
        undefined,
        'exp',
        abPriority
      );
      const timestampOffsetSeconds =
        getTimestampOffset(getState(), Accuracy.SECOND) || 0;
      const now = getCurrentUnixTimestamp({
        offsetSeconds: timestampOffsetSeconds,
      });

      if (now > abTokenExp) {
        return dispatch(addModal({ id: 'PriceRefreshModal' }));
      }
    }

    const ctaUrlData = getMessageData(getState(), id, 'ctaUrlData');
    if (shouldOpenMessagePackUpsellingModal && ctaUrlData) {
      await dispatch(
        fetchOembedData({
          url: ctaUrlData.ctaUrl,
          language: getCurrentLanguage(),
        })
      );
      const oembedData = getOperationData(
        getState(),
        ['oembed'],
        ctaUrlData.ctaUrl
      );
      if (oembedData) {
        return dispatch(
          toggleModal({
            modalId: 'MessagePackUpsellingModal',
            payload: {
              messageId: id,
              productId: ctaUrlData.productId,
              drmFeatureName,
            },
            isOpen: true,
          })
        );
      }
    }

    if (shouldCheckPrice) {
      const currentPrice = getMessageUnlockPrice(getState(), id);
      await dispatch(fetchMessageDetail({ id, forceRefetch: true }));
      const latestPrice = getMessageUnlockPrice(getState(), id);
      if (currentPrice !== latestPrice) {
        trackMixpanel({
          type: EventTypes.USER_DEBUG_MESSAGE_PRICE,
          payload: {
            'message.id': id,
            'message.currentPrice': currentPrice,
            'message.currentPriceType': typeof currentPrice,
            'message.latestPrice': latestPrice,
            'message.latestPriceType': typeof latestPrice,
          },
        });
        return dispatch(
          toggleModal({
            modalId: 'PriceRefreshModal',
            isOpen: true,
          })
        );
      }
    }

    const selectPath = ['messages', id, 'unlock'];

    const url = getResourceUrl({ endpoint: `/messages/${id}/unlock` });
    const fetchOptions = {
      method: 'PUT',
      headers: {
        ...getHeaders(),
        Authorization: `Bearer ${token}`,
      },
    };

    dispatch({ type: SET_NETWORKING_FETCHING, payload: { selectPath } });

    try {
      const unlockPrice = getMessageUnlockPrice(getState(), id);

      if (unlockPrice <= 0) {
        throw new Error('The free message cannot be unlocked');
      }

      let response = await fetch(url.href, fetchOptions);

      if (!response.ok) {
        response = await handleFetchError({
          response,
          dispatch,
          getState,
          fetchOptions,
          fetchUrl: url,
        });
      }

      dispatch(sendMessageUnlockEvent({ id }));

      dispatch(deductDiamond({ price: unlockPrice }));

      dispatch({ type: SET_NETWORKING_SUCCESS, payload: { selectPath } });
      await dispatch(
        fetchDrmLicenseAvailability({
          messageId: id,
          keySystem: getKeySystem(),
        })
      );

      const isOnFreeZoneDetail = freezoneDetailReg.test(
        getRouterData(getState(), 'location')?.pathname
      );
      const isUnlocked = getMessageData(getState(), id, 'canViewMedia');

      if (isOnFreeZoneDetail && isUnlocked) {
        dispatch(replace(`/post/${id}`));
      }
    } catch (error) {
      return dispatch({
        type: SET_NETWORKING_ERROR,
        payload: { selectPath, error },
      });
    }
  };

export default sendUnlockMessage;

/**
 * Send message unlock event.
 * @kind action
 * @param {string} {id} - message id.
 * @returns {Promise} Action promise.
 */
const sendMessageUnlockEvent =
  ({ id } = {}) =>
  async (dispatch, getState) => {
    const messageType = getMessageData(getState(), id, 'type');
    const shouldSendToDataLayer = !['chat', 'reply'].includes(messageType);

    if (!shouldSendToDataLayer) return dispatch({ type: '' });

    try {
      log('gtm message unlock event sending');

      if (typeof window !== 'undefined' && window['gtmDataLayer']) {
        const meUsername = getMeData(getState(), 'username');
        const senderId = getMessageData(getState(), id, 'senderId');
        const senderUsername = getUserData(getState(), senderId, 'username');
        const unlockPrice = getMessageUnlockPrice(getState(), id);
        const title = getMessageDataWithVariable(
          getState(),
          id,
          'originalTitle',
          `${VariableName.USERNAME}:${meUsername}`
        );
        const description = getMessageDataWithVariable(
          getState(),
          id,
          'originalCaptionText',
          `${VariableName.USERNAME}:${meUsername}`
        );
        const gtmEventPayload = {
          event: 'message_unlock',
          message: {
            id,
            video_provider_id: senderId,
            video_provider_name: senderUsername,
            video_title: title,
            video_description: description,
            video_url: `https://swag.live/post/${id}`,
            value: unlockPrice,
          },
        };

        const expiresAtUnix = getMessageData(getState(), id, 'expiresAtUnix');
        if (expiresAtUnix) {
          gtmEventPayload.message.expire_time = expiresAtUnix;
        }

        const categories = getMessageData(getState(), id, 'categories') || [];
        if (categories.length) {
          gtmEventPayload.message.video_tags = categories.join(',');
          categories.forEach((category, index) => {
            gtmEventPayload.message[`video_tag_${index}`] = category;
          });
        }

        window['gtmDataLayer'].push({ event: 'message_unlock', message: null }); // Clear the previous message object.
        window['gtmDataLayer'].push(gtmEventPayload);

        log('gtm message unlock event payload', {
          gtmEventPayload,
        });
      }
      log('gtm message unlock sent');
    } catch (error) {
      log('gtm message unlock event send failed', {
        error,
      });
    }
  };
